// Request util for api call
import request from '~/utils/request';
import { appendSortParams } from '~/utils/sort-params';

// Actions Types
import {
  SET_QR_CODES_DATA,
  SET_QR_CODES_LOADER,
  CREATE_QR_CODE,
  UPDATE_QR_CODE,
  DELETE_QR_CODE,
  UPDATE_QR_CODE_STATUS,
  SET_QR_CODE_SCANNED_CONTACTS_DATA,
  SET_QR_CODE_SCANNED_CONTACTS_LOADER,
} from './action-types';

// Utils
import { DEFAULT_PAGINATION } from '~/utils/constants';

/**
 * Fetch all QR codes
 * GET /qr-code-instances
 */
const fetchQrCodes = (payload) => async (dispatch) => {
  try {
    dispatch({ type: SET_QR_CODES_LOADER, payload: true });

    let url = 'qr-code-instances';

    let queryPayload = {
      page: payload?.pagination?.page ? payload.pagination.page : DEFAULT_PAGINATION.page,
      pageSize: payload?.pagination?.pageSize
        ? payload.pagination.pageSize
        : DEFAULT_PAGINATION.pageSize,
    };

    if (payload?.search?.length) {
      queryPayload.search = payload.search;
    }

    if (payload?.engagementType) {
      queryPayload.engagementType = payload.engagementType;
    }

    appendSortParams(queryPayload, payload);

    const response = await request.get(url, queryPayload);

    if (response.status === 200) {
      const qrCodesData = {
        rows: response.data.data.rows || response.data.data,
        count: response.data.data.count || response.data.data.length,
        to: response.data.data.to,
        from: response.data.data.from,
        currentPage: response.data.data.currentPage,
        lastPage: response.data.data.lastPage,
        perPage: response.data.data.perPage,
        totalRecordsInDB: response.data?.data?.totalRecordsInDB || response.data?.data?.total,
      };
      dispatch({ type: SET_QR_CODES_DATA, payload: qrCodesData });
    }

    return response;
  } catch (error) {
    return error;
  } finally {
    dispatch({ type: SET_QR_CODES_LOADER, payload: false });
  }
};

/**
 * Create new QR code
 * POST /qr-code-instances
 */
const createQrCode = (payload) => async (dispatch) => {
  try {
    const response = await request.post('qr-code-instances', payload);
    return response;
  } catch (error) {
    return error.response;
  }
};

/**
 * Update QR code
 * PUT /qr-code-instances/:id
 */
const updateQrCode = (id, payload) => async (dispatch) => {
  try {
    const response = await request.put(`qr-code-instances/${id}`, payload);

    if (response.status === 200) {
      dispatch({ type: UPDATE_QR_CODE, payload: response.data.data });
    }

    return response;
  } catch (error) {
    return error.response;
  }
};

/**
 * Delete QR code
 * DELETE /qr-code-instances/:id
 */
const deleteQrCode = (id) => async (dispatch) => {
  try {
    const response = await request.delete(`/qr-code-instances/${id}`);

    if (response.status === 200) {
      dispatch({ type: DELETE_QR_CODE, payload: { id } });
    }

    return response;
  } catch (error) {
    return error.response;
  }
};

/**
 * Update QR code status
 * PATCH /qr-code-instances/:id/status
 */
const updateQrCodeStatus = (id, payload) => async (dispatch) => {
  try {
    const response = await request.patch(`qr-code-instances/${id}/status`, payload);

    if (response.status === 200) {
      dispatch({ type: UPDATE_QR_CODE_STATUS, payload: response.data.data });
    }

    return response;
  } catch (error) {
    return error.response;
  }
};

/**
 * Get scanned contacts for a QR code
 * GET /qr-code-instances/:id/scanned-contacts
 */
const fetchQrCodeScannedContacts = (id, payload) => async (dispatch) => {
  try {
    dispatch({ type: SET_QR_CODE_SCANNED_CONTACTS_LOADER, payload: true });

    let url = `qr-code-instances/${id}/scanned-contacts`;

    let queryPayload = {
      page: payload?.page ? payload.page : DEFAULT_PAGINATION.page,
      pageSize: payload?.pageSize ? payload.pageSize : DEFAULT_PAGINATION.pageSize,
    };

    if (payload?.engagementType) {
      queryPayload.engagementType = payload.engagementType;
    }

    appendSortParams(queryPayload, payload);

    const response = await request.get(url, queryPayload);

    if (response.status === 200) {
      const scannedContactsData = {
        rows: response.data.data.rows || response.data.data,
        count: response.data.data.count || response.data.data.length,
        to: response.data.data.to,
        from: response.data.data.from,
        currentPage: response.data.data.currentPage,
        lastPage: response.data.data.lastPage,
        perPage: response.data.data.perPage,
        totalRecordsInDB: response.data?.data?.totalRecordsInDB || response.data?.data?.total,
      };
      dispatch({ type: SET_QR_CODE_SCANNED_CONTACTS_DATA, payload: scannedContactsData });
    }

    return response;
  } catch (error) {
    return error;
  } finally {
    dispatch({ type: SET_QR_CODE_SCANNED_CONTACTS_LOADER, payload: false });
  }
};

const uploadFile = async (file) => {
  try {
    const formData = new FormData();
    formData.append('image', file);
    const response = await request.post('templates/uploadFile', formData);

    return response?.data?.data?.filePath;
  } catch (error) {
    return error?.response;
  }
};

export {
  fetchQrCodes,
  createQrCode,
  updateQrCode,
  deleteQrCode,
  updateQrCodeStatus,
  fetchQrCodeScannedContacts,
  uploadFile,
};
